import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { useSEO } from '../hooks/useSEO';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="glass-card p-5 space-y-2">
    <p className="text-white/90 font-semibold">{title}</p>
    <div className="text-white/55 text-sm leading-relaxed space-y-2">{children}</div>
  </div>
);

const About: React.FC = () => {
  useSEO(
    'About — Purgr',
    'Purgr is a free, privacy-first tool that helps Instagram users see who doesn\'t follow them back — no login required, no data ever leaves your device.'
  );

  return (
    <PageLayout title="About Purgr">
      <div className="space-y-4">
        <Section title="What is Purgr?">
          <p>
            Purgr is a free browser-based tool that lets you compare your Instagram following and followers lists
            to see exactly who isn't following you back — without ever logging in to Instagram or uploading
            your data to any server.
          </p>
          <p>
            Everything happens locally in your browser. Your files are read by your own device, processed in
            memory, and gone the moment you close or refresh the tab.
          </p>
        </Section>

        <Section title="Who made it?">
          <p>
            Purgr was built and is maintained by Rianna — a solo indie developer. It started as a personal
            project after getting frustrated with shady "who unfollowed me" apps that require your Instagram
            password (and definitely shouldn't).
          </p>
          <p>
            The goal was simple: build something that actually works, respects your privacy, and doesn't cost
            anything.
          </p>
        </Section>

        <Section title="How it works">
          <ol className="space-y-2 list-decimal list-inside">
            <li>
              <strong className="text-white/80">Download your Instagram data</strong> — Instagram lets you
              export your own data as JSON files from your account settings. No third-party access needed.
            </li>
            <li>
              <strong className="text-white/80">Upload the two files</strong> — Drop your{' '}
              <code className="text-white/70 bg-white/5 px-1 rounded">following.json</code> and{' '}
              <code className="text-white/70 bg-white/5 px-1 rounded">followers_1.json</code> files into Purgr.
            </li>
            <li>
              <strong className="text-white/80">See your results instantly</strong> — Purgr compares the two
              lists and shows you who's not following back, who you don't follow back, and your mutuals.
            </li>
          </ol>
        </Section>

        <Section title="Our commitment to privacy">
          <ul className="space-y-1 list-disc list-inside">
            <li>No Instagram login or credentials — ever.</li>
            <li>Your files are never uploaded to any server.</li>
            <li>No accounts required. No sign-up. No tracking of individuals.</li>
            <li>The only thing stored in your browser is your cookie preference.</li>
          </ul>
          <p>
            Read the full <Link to="/privacy" className="text-white/60 hover:text-white/90 underline">Privacy Policy</Link> for
            complete details.
          </p>
        </Section>

        <Section title="Keeping it free">
          <p>
            Purgr is free to use and always will be. It's supported by Google AdSense ads (shown only with
            your consent) and optional affiliate links to Instagram growth tools. If you find it useful, you
            can also leave a tip via Ko-fi — it genuinely helps keep the project alive.
          </p>
        </Section>

        <Section title="Get in touch">
          <p>
            Have a question, found a bug, or want to share feedback?{' '}
            <Link to="/contact" className="text-white/60 hover:text-white/90 underline">Reach out on the Contact page</Link>.
          </p>
          <p>
            Purgr is not affiliated with, endorsed by, or connected to Instagram or Meta Platforms, Inc.
          </p>
        </Section>
      </div>
    </PageLayout>
  );
};

export default About;
